import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@/store';

// Base selectors
export const selectReturnsState = (state: RootState) => state.returns;

export const selectAllReturnIds = (state: RootState) => state.returns.allIds;

export const selectReturnsById = (state: RootState) => state.returns.byId;

export const selectReturnFilters = (state: RootState) => state.returns.filters;

export const selectSelectedReturnId = (state: RootState) => state.returns.selectedReturnId;

export const selectReturnsLoading = (state: RootState) => state.returns.isLoading;

export const selectReturnsError = (state: RootState) => state.returns.error;

// Memoized selectors
export const selectAllReturns = createSelector(
  [selectReturnsById, selectAllReturnIds],
  (byId, allIds) => allIds.map((id) => byId[id])
);

export const selectReturnById = (id: string) =>
  createSelector([selectReturnsById], (byId) => byId[id]);

export const selectSelectedReturn = createSelector(
  [selectReturnsById, selectSelectedReturnId],
  (byId, selectedId) => (selectedId ? byId[selectedId] : null)
);

// Filtered returns
export const selectFilteredReturns = createSelector(
  [selectAllReturns, selectReturnFilters],
  (returns, filters) => {
    let filtered = [...returns];

    // Filter by status
    if (filters.status) {
      filtered = filtered.filter((returnItem) => returnItem.status === filters.status);
    }

    // Filter by sale ID
    if (filters.saleId) {
      filtered = filtered.filter((returnItem) => returnItem.sale_id === filters.saleId);
    }

    // Filter by date range
    if (filters.dateRange) {
      const startDate = new Date(filters.dateRange.start);
      const endDate = new Date(filters.dateRange.end);
      filtered = filtered.filter((returnItem) => {
        const returnDate = new Date(returnItem.return_date);
        return returnDate >= startDate && returnDate <= endDate;
      });
    }

    // Filter by search (return ID or sale ID)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (returnItem) =>
          returnItem.id.toLowerCase().includes(searchLower) ||
          returnItem.sale_id.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }
);

// Returns by status
export const selectPendingReturns = createSelector([selectAllReturns], (returns) =>
  returns.filter((returnItem) => returnItem.status === 'pending')
);

export const selectApprovedReturns = createSelector([selectAllReturns], (returns) =>
  returns.filter((returnItem) => returnItem.status === 'approved')
);

export const selectRefundedReturns = createSelector([selectAllReturns], (returns) =>
  returns.filter((returnItem) => returnItem.status === 'refunded')
);

export const selectRejectedReturns = createSelector([selectAllReturns], (returns) =>
  returns.filter((returnItem) => returnItem.status === 'rejected')
);

// Returns by sale
export const selectReturnsBySaleId = (saleId: string) =>
  createSelector([selectAllReturns], (returns) =>
    returns.filter((returnItem) => returnItem.sale_id === saleId)
  );

// Statistics
export const selectReturnsStats = createSelector([selectAllReturns], (returns) => {
  const now = new Date();
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const thisMonthReturns = returns.filter(
    (returnItem) => new Date(returnItem.return_date) >= firstDayOfMonth
  );

  const totalReturnsThisMonth = thisMonthReturns.length;
  const totalRefundThisMonth = thisMonthReturns.reduce(
    (sum, returnItem) => sum + returnItem.total_refund,
    0
  );
  const pendingCount = returns.filter((returnItem) => returnItem.status === 'pending').length;

  return {
    totalReturnsThisMonth,
    totalRefundThisMonth,
    pendingCount,
    totalReturns: returns.length,
  };
});
